import React, { Component } from 'react'
import { inject, observer } from 'mobx-react'
import { action, extendObservable, toJS } from 'mobx'
import PropTypes from 'prop-types'
import { Formik, Form, Field } from 'formik'
import yup from 'yup'
import _ from 'lodash'

import get from 'lodash/get'

import { userApi, expertApi, eplApi } from '@atheneum/api'
import { shouldHideExpertInfo } from '@atheneum/api/dist/lib'
import EplExperiences from './EplExperiences'
import EplPaymentRates from './EplPaymentRates'
import ExpertApplicationInfoSection from './ExpertApplicationInfoSection'
import ScreeningSection from './ScreeningSection'
import Button from '@components/Button'
import Checkbox from '@components/Form/Checkbox'
import { showDialog, isCancellationError } from '@components/Dialog'
import RichTextEditor from '@components/Form/RichTextEditor'
import WarningIcon from '@components/Icon/Warning'
import InfoBox from '@components/InfoBox'
import Languages from '@components/Languages'
import Spinner from '@components/spinner'
import Tooltip from '@components/Tooltip'
import RecordingsSection from './RecordingsSection'
import AvailabilitySection from './AvailabilitySection'

import ToggleVisibility from './ToggleVisibility'

class EditEpl extends Component {
  static propTypes = {
    epl: PropTypes.object,
    projectStore: PropTypes.object,
    staticData: PropTypes.object,
    modalStore: PropTypes.object,
    growlStore: PropTypes.object
  }

  constructor (props) {
    super(props)

    const epl = this.props.epl

    extendObservable(this, {
      loading: false,
      error: null,
      hiddenExperienceIds: [],
      languages: [],
      positions: []
    })

    if (epl) {
      const eplId = get(epl, 'epl.id')
      const expertId = get(epl, 'epl.expert.id')
      this.loadAdditionalData(expertId, eplId)

      this.setFieldsHiddenToExpert = this.setFieldsHiddenToExpert.bind(this)
      this.onBulkToggleRecordingsHiddenToExpert = this.onBulkToggleRecordingsHiddenToExpert.bind(this)
      this.setExperiencesHiddenToExpert = this.setExperiencesHiddenToExpert.bind(
        this
      )
    }
  }

  componentDidMount () {
    const { epl, eplStore } = this.props

    eplStore.getRecordingsFromEplHash(epl.epl.hash)

    let fieldsHiddenToExpert = get(epl, 'epl.fieldsHiddenToExpert')

    if (fieldsHiddenToExpert) {
      const fieldsHiddenToExpertArray = JSON.parse(fieldsHiddenToExpert)

      fieldsHiddenToExpert = fieldsHiddenToExpertArray.reduce(
        (results, field) => {
          results[field] = true
          return results
        },
        {}
      )

      const matchFields = ['recordingAudio', 'recordingVideo', 'recordingTranscript']
      const recordingsHiddenToExpert = fieldsHiddenToExpertArray.filter(field => matchFields.includes(field))
      const bulkToggleRecordingsHiddenToExpert = recordingsHiddenToExpert.length === matchFields.length

      this.setState({ fieldsHiddenToExpert, bulkToggleRecordingsHiddenToExpert })
    }
  }

  state = {
    error: null,
    fieldsHiddenToExpert: {},
    experiencesHiddenToExpert: {},
    bulkToggleRecordingsHiddenToExpert: false
  }

  loadAdditionalData = action((expertId, eplId) => {
    this.loading = true
    this.error = false

    return Promise.all([
      userApi.getLanguages(expertId),
      expertApi.getPositions(expertId, eplId),
      eplApi.getHiddenExperiences(eplId),
      eplApi.getHiddenExperienceFields(eplId)
    ])
      .then(
        ([languages, positions, hiddenExperiences, hiddenExperienceFields]) => {
          if (
            hiddenExperienceFields[0] &&
            hiddenExperienceFields[0].fields_hidden_to_expert
          ) {
            this.setState({
              experiencesHiddenToExpert: JSON.parse(
                hiddenExperienceFields[0].fields_hidden_to_expert
              )
            })
          }

          this.hiddenExperienceIds.replace(hiddenExperiences.map(e => e.id))
          this.languages.replace(languages)
          this.positions.replace(positions)
          this.loading = false
        }
      )
      .catch(err => {
        const message = err.message || 'Unknown error occurred'
        this.loading = false
        this.error = message

        return showDialog({
          title: 'Failed fetching data',
          message,
          type: 'error'
        }).catch(err => isCancellationError(err))
      })
  })

  getInitialFormValues () {
    const { staticData, epl } = this.props
    const { PRICINGS } = staticData.CONST
    const eplData = Object.assign(
      { relevantExperienceId: epl.epl.relevantExperience.experienceId },
      toJS(epl.epl)
    )

    const fee = eplData.fee
    const honorarium = eplData.expert.hourlyRate || eplData.honorarium
    const industryExpertise = epl.epl.industryExpertise
    const screening = epl.epl.screening || ''
    const pricingId = eplData.pricingId || PRICINGS.PER_HOUR

    const formData = {
      ...eplData,
      industryExpertise,
      screening,
      honorarium,
      pricingId,
      fee,
      overwriteExpertIndustryExpertise: false
    }

    return formData
  }

  getFormValidationSchema () {
    const { EPL_STATUSES } = this.props.staticData.CONST
    const statusIds = [EPL_STATUSES.INTERVIEWED, EPL_STATUSES.NOT_INTERVIEWED]

    return yup.object().shape({
      eplStatusId: yup.number().integer(),
      industryExpertise: yup
        .string()
        .required('Industry experience is required')
        .typeError('Industry experience is required'),
      overwriteExpertIndustryExpertise: yup.boolean(),
      allowRecordings: yup.boolean(),
      honorariumCurrencyId: yup
        .number()
        .integer()
        .nullable()
        .when(['eplStatusId', 'free'], (eplStatusId, free, schema) => {
          if (!free && statusIds.includes(eplStatusId)) {
            return schema.required('Required')
          } else {
            return schema
          }
        }),
      honorarium: yup
        .number()
        .nullable()
        .when(['eplStatusId', 'free'], (eplStatusId, free, schema) => {
          if (!free && statusIds.includes(eplStatusId)) {
            return schema.required('Required')
          } else {
            return schema
          }
        }),
      free: yup.boolean(),
      feeCurrencyId: yup
        .number()
        .integer()
        .nullable()
        .when('eplStatusId', (eplStatusId, schema) => {
          if (statusIds.includes(eplStatusId)) {
            return schema.required('Required')
          } else {
            return schema
          }
        }),
      fee: yup
        .number()
        .nullable()
        .when('eplStatusId', (eplStatusId, schema) => {
          if (statusIds.includes(eplStatusId)) {
            return schema.required('Required')
          } else {
            return schema
          }
        }),
      credits: yup.number().nullable(),
      pricingId: yup
        .number()
        .integer()
        .nullable()
        .when('eplStatusId', (eplStatusId, schema) => {
          if (statusIds.includes(eplStatusId)) {
            return schema.required('Pricing is required')
          } else {
            return schema
          }
        })
    })
  }

  handleFormSubmission = (values, actions) => {
    const filteredFieldsHiddenToExpert = JSON.stringify(
      _.reduce(
        this.state.fieldsHiddenToExpert,
        (result, field, key) => {
          if (field) {
            result.push(key)
          }

          return result
        },
        []
      )
    )

    const eplId = get(this.props, 'epl.epl.id')
    const relevantExperienceId = values.relevantExperienceId
    values.industryExpertise = values.industryExpertise !== null ? values.industryExpertise.replaceAll("...", "").replaceAll("..", "") : values.industryExpertise;

    if (!relevantExperienceId) {
      const error = 'Must select relevant position.'

      this.setState({ error })
      this.props.growlStore.showError(error)

      return actions.setSubmitting(false)
    }

    this.setState({ error: null })
    const hiddenExperienceIds = this.hiddenExperienceIds

    return eplApi
      .save(
        eplId,
        { ...values },
        hiddenExperienceIds,
        filteredFieldsHiddenToExpert,
        JSON.stringify(this.state.experiencesHiddenToExpert)
      )
      .then(() => {
        this.props.epl.get()
        this.props.modalStore.close()
      })
      .catch(error => {
        this.setState({ error: error.message })
        this.props.growlStore.showError(error)
        actions.setSubmitting(false)
      })
  }

  setFieldsHiddenToExpert (field, value) {
    this.setState({
      fieldsHiddenToExpert: {
        ...this.state.fieldsHiddenToExpert,
        [field]: value
      }
    })
  }

  onBulkToggleRecordingsHiddenToExpert (fieldNames) {
    const value = !this.state.bulkToggleRecordingsHiddenToExpert

    const fieldsHiddenToExpert = {
      ...this.state.fieldsHiddenToExpert
    }

    fieldNames.forEach(fieldName => fieldsHiddenToExpert[fieldName] = value);
    
    this.setState({
      bulkToggleRecordingsHiddenToExpert: value,
      fieldsHiddenToExpert
    })
  }

  setExperiencesHiddenToExpert (id, field, value) {
    const updatedExperiencesHiddenToExpert = {
      ...this.state.experiencesHiddenToExpert,
      [id]: {
        ...this.state.experiencesHiddenToExpert[id],
        [field]: value
      }
    }

    this.setState({
      experiencesHiddenToExpert: updatedExperiencesHiddenToExpert
    })
  }

  renderStatusBorder () {
    const { staticData, epl } = this.props
    const eplStatusId = get(epl, 'epl.eplStatusId')
    const color = staticData.getStatusColor(eplStatusId)
    const classes = `edit-epl__status-border edit-epl__status-border--${color}`

    return <div className={classes} />
  }

  renderLoader () {
    return (
      <div className='edit-epl expert-form'>
        {this.renderStatusBorder()}
        <Spinner />
      </div>
    )
  }

  render () {
    if (this.loading) {
      return this.renderLoader()
    }

    const { modalStore, epl, staticData, eplStore } = this.props

    return (
      <div>
        <Formik
          initialValues={this.getInitialFormValues()}
          validationSchema={this.getFormValidationSchema()}
          onSubmit={this.handleFormSubmission}
          render={form => (
            <div className='edit-epl expert-form'>
              <Form onSubmit={form.handleSubmit}>
                {this.renderStatusBorder()}
                <h1 className='expert-form__title'>
                  {epl.epl.expert.fullName}
                </h1>
                <h2 className='expert-form__subtitle expert-form__subtitle--line-height30'>
                  {epl.position},<br />
                  {epl.formattedExperience}
                </h2>
                {this.state.error ? (
                  <InfoBox theme='red' icon={<WarningIcon />}>
                    {this.state.error}
                  </InfoBox>
                ) : null}
                <ExpertApplicationInfoSection
                  epl={epl}
                  staticData={staticData}
                />
                <ScreeningSection
                  epl={epl}
                  form={form}
                  staticData={staticData}
                  toggleFieldsHiddenToExpert={() =>
                    this.setFieldsHiddenToExpert(
                      'screening',
                      !this.state.fieldsHiddenToExpert['screening']
                    )
                  }
                  fieldHiddenToExpert={
                    this.state.fieldsHiddenToExpert['screening']
                  }
                />
                <AvailabilitySection
                  form={form}
                  staticData={staticData}
                  toggleFieldsHiddenToExpert={() =>
                    this.setFieldsHiddenToExpert(
                      'expertAvailability',
                      !this.state.fieldsHiddenToExpert['expertAvailability']
                    )
                  }
                  fieldHiddenToExpert={
                    this.state.fieldsHiddenToExpert['expertAvailability']
                  }
                />
                <div className='expert-form__section'>
                  <h2 className='expert-form__subtitle'>Relevant position</h2>
                  <EplExperiences
                    form={form}
                    positions={this.positions}
                    hiddenExperienceIds={this.hiddenExperienceIds}
                    staticData={staticData}
                    experiencesHiddenToExpert={
                      this.state.experiencesHiddenToExpert
                    }
                    setExperiencesHiddenToExpert={
                      this.setExperiencesHiddenToExpert
                    }
                  />
                </div>
                <div className='expert-form__section'>
                  <h2 className='expert-form__subtitle'>Industry experience</h2>
                  <div className='field-toggle-group'>
                    <Field
                      name='industryExpertise'
                      placeholder='Industry experience'
                      toolbarRight
                      skipWrapper
                      component={RichTextEditor}
                    />
                    <ToggleVisibility
                      cyData={'toggle-hide-industryExpertise'}
                      invisible={
                        this.state.fieldsHiddenToExpert['industryExpertise']
                      }
                      onToggle={() =>
                        this.setFieldsHiddenToExpert(
                          'industryExpertise',
                          !this.state.fieldsHiddenToExpert['industryExpertise']
                        )
                      }
                    />
                  </div>
                  <div>
                    <Field
                      name='overwriteExpertIndustryExpertise'
                      label={
                        <span>
                          Overwrite industry experience on Expert's Profile
                          <Tooltip
                            tooltip='Overwriting the Industry Experience on the Expert Profile cannot be undone.
                                                    However, the “original” text will still be available in the “Expert Experience”
                                                    field (if not changed by the Expert themselves).'
                          />
                        </span>
                      }
                      component={Checkbox}
                    />
                  </div>
                </div>
                <div className='expert-form__section expert-form__section--horizontal'>
                  <div className='expert-form__section-child'>
                    <h2>Languages</h2>
                    <div className='field-toggle-group'>
                      <Languages languages={this.languages} />
                      <ToggleVisibility
                        invisible={this.state.fieldsHiddenToExpert['languages']}
                        cyData={'toggle-hide-languages'}
                        onToggle={() =>
                          this.setFieldsHiddenToExpert(
                            'languages',
                            !this.state.fieldsHiddenToExpert['languages']
                          )
                        }
                      />
                    </div>
                  </div>
                </div>
                <div className='expert-form__section expert-form__section--horizontal'>
                  <div className='expert-form__section-child'>
                    <h2>Fees</h2>
                    <div className='field-toggle-group'>
                      <EplPaymentRates
                        form={form}
                        projectStore={this.props.projectStore}
                      />
                      <ToggleVisibility
                        invisible={this.state.fieldsHiddenToExpert['fee']}
                        cyData={'toggle-hide-fee'}
                        onToggle={() =>
                          this.setFieldsHiddenToExpert(
                            'fee',
                            !this.state.fieldsHiddenToExpert['fee']
                          )
                        }
                      />
                    </div>
                  </div>
                </div>
                <RecordingsSection
                  form={form}
                  epl={epl}
                  toggleFieldsHiddenToExpert={fieldName =>
                    this.setFieldsHiddenToExpert(
                      fieldName,
                      !this.state.fieldsHiddenToExpert[fieldName]
                    )
                  }
                  bulkToggleRecordingsHiddenToExpert={this.state.bulkToggleRecordingsHiddenToExpert}
                  onBulkToggleRecordingsHiddenToExpert={fieldNames => this.onBulkToggleRecordingsHiddenToExpert(fieldNames)}
                  fieldHiddenToExpert={{
                    recordingAudio: this.state.fieldsHiddenToExpert['recordingAudio'],
                    recordingVideo: this.state.fieldsHiddenToExpert['recordingVideo'],
                    recordingTranscript: this.state.fieldsHiddenToExpert['recordingTranscript'],
                    recordings: this.state.fieldsHiddenToExpert['recordings']
                  }}
                  recordingEnd={eplStore.recordings.length ? eplStore.recordings[0].recordingEnd : ''}
                />
                <div className='expert-form__section p-b-0'>
                  <Button
                    primary
                    submit
                    onClick={this.onSubmit}
                    disabled={form.isSubmitting}
                  >
                    Save
                  </Button>
                  <Button
                    onClick={modalStore.close}
                    disabled={form.isSubmitting}
                  >
                    Cancel
                  </Button>
                </div>
              </Form>
            </div>
          )}
        />
      </div>
    )
  }
}

EditEpl.defaultProps = {
  hideButtons: true,
  showCloseIcon: false,
  shouldCloseOnOverlayClick: true
}

export default inject('growlStore', 'modalStore', 'eplStore', 'staticData')(
  observer(EditEpl)
)
